import { useEffect, useRef, useState, memo, useCallback } from "react";
import { getOffsetProc } from "../chessUtils/util";
import styles from "./Piece.module.css";
import { ANIMATION_DURATION, Z_OVER } from "./animations";
import React from "react";
import { COLOR_LETTERS, PIECE_LETTERS } from "../chessUtils/fen";

function easeInOutQuad(
  elapsed: number,
  initialValue: number,
  goal: number,
  duration: number
): number {
  if ((elapsed /= duration / 2) < 1) {
    return ((goal - initialValue) / 2) * elapsed * elapsed + initialValue;
  }
  return (
    (-(goal - initialValue) / 2) * (--elapsed * (elapsed - 2) - 1) +
    initialValue
  );
}

function rafAnimation(
  start: { x: number; y: number },
  goal: { x: number; y: number },
  piece: HTMLDivElement | null,
  animating: React.RefObject<boolean>,
  onAnimationEnd?: () => void
) {
  if (!piece) {
    return;
  }
  const startTime = performance.now();
  loop();

  function loop() {
    const time = performance.now() - startTime;

    if (!animating.current) {
      return;
    }

    if (time < ANIMATION_DURATION) {
      let x = easeInOutQuad(time, start.x, goal.x, ANIMATION_DURATION);
      let y = easeInOutQuad(time, start.y, goal.y, ANIMATION_DURATION);
      piece.style.transform = `translate(${x}%, ${y}%)`;
      requestAnimationFrame(loop);
    } else {
      piece.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
      onAnimationEnd?.();
    }
  }
}

interface PieceData {
  id: string;
  role: "king" | "queen" | "rook" | "bishop" | "knight" | "pawn";
  color: "white" | "black";
}

interface PieceProps {
  piece: PieceData;
  currentKey: string;
  pieceRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  canDrag: boolean;
  playingWhite: boolean;
  animate: boolean;
  zIndex: number;
  chessSet: string;
  onAnimationEnd?: () => void | null;
  boardSize: number;
}

function Piece({
  piece,
  currentKey,
  pieceRefs,
  canDrag,
  playingWhite,
  animate,
  zIndex,
  chessSet,
  onAnimationEnd = null,
  boardSize,
}: PieceProps) {
  const { id, role, color } = piece;
  const lastKey = useRef(currentKey);
  const animating = useRef(animate);
  const animRef = useRef(null);

  const [offset, setOffset] = useState(getOffsetProc(currentKey, playingWhite));
  useEffect(() => {
    if (lastKey.current != currentKey && animate) {
      const pieceEl = pieceRefs.current[id];
      if (!pieceEl) {
        return;
      }
      animating.current = true;
      const start = getOffsetProc(lastKey.current, playingWhite);
      const goal = getOffsetProc(currentKey, playingWhite);
      const squarePx = boardSize / 8;
      const startPx = {
        x: (start.x * squarePx) / 100,
        y: (start.y * squarePx) / 100,
      };
      const goalPx = {
        x: (goal.x * squarePx) / 100,
        y: (goal.y * squarePx) / 100,
      };

      try {
        animRef.current = pieceEl.animate(
          [
            { transform: `translate3d(${startPx.x}px, ${startPx.y}px, 0)` },
            { transform: `translate3d(${goalPx.x}px, ${goalPx.y}px, 0)` },
          ],
          {
            duration: ANIMATION_DURATION,
            easing: "ease-in-out",
            fill: "forwards",
          }
        );

        animRef.current.onfinish = () => {
          pieceEl.style.transform = `translate3d(${goalPx.x}px, ${goalPx.y}px, 0)`;
          onAnimationEnd?.();
        };
      } catch (error) {
        rafAnimation(
          start,
          goal,
          pieceEl,
          animating,
          onAnimationEnd || undefined
        );
      }
    } else {
      const goal = getOffsetProc(currentKey, playingWhite);
      setOffset(goal);
    }
    lastKey.current = currentKey;
  }, [currentKey, playingWhite, animate, boardSize]);

  useEffect(() => {
    if (!animate) {
      animating.current = false;
      if (animRef.current) {
        animRef.current.cancel();
      }
      const goal = getOffsetProc(currentKey, playingWhite);
      const pieceEl = pieceRefs.current[id];
      if (pieceEl) {
        pieceEl.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
      }
    }
  }, [animate]);

  const classes = [];
  const pieceCode = COLOR_LETTERS[color] + PIECE_LETTERS[role];
  classes.push(styles.piece);
  if (!canDrag) {
    classes.push(styles.opponent);
  }

  const pieceImageUrl = `/images/pieces/${chessSet}/${pieceCode}.svg`;

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      pieceRefs.current[id] = el;
    },
    [id]
  );

  return (
    <div
      key={piece.id}
      ref={setRef}
      style={{
        zIndex,
        transform: `translate(${offset.x}%, ${offset.y}%)`,
        backgroundImage: `url(${pieceImageUrl})`,
      }}
      className={classes.join(" ")}
    ></div>
  );
}

export default memo(Piece);
